import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, CheckCircle2, Wrench, Home as HomeIcon, Hammer, Trees, DoorOpen, Star, ChevronLeft, ChevronRight, Award, Users, Plus } from "lucide-react";
import { services, whyChooseUs, companyInfo } from "../data";
import ReviewSlider from "../components/ReviewSlider";
import ContactForm from "../components/ContactForm";

// Map ikon-navne til Lucide komponenter
const iconMap = {
  Wrench: Wrench,
  Home: HomeIcon,
  Hammer: Hammer,
  Trees: Trees,
  DoorOpen: DoorOpen
};

export default function Home() {
  const [latestProjects, setLatestProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);

  const heroData = {
    image: "https://assets.auxodev.dk/assets/531e2906-0b4f-4a2d-bfb4-6b0bf7bd2bc6/image00005-rez_1.jpg",
    tag: "Kvalitet & Solidt Håndværk",
    title: "Professionelt byggeri med fokus på kvalitet og håndværk",
    description: "Din pålidelige partner i Janderup og omegn med fokus på kvalitet og solidt håndværk."
  };

  const getResizedUrl = (url, width = 1200) => {
    return url.replace("https://assets.auxodev.dk/", `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=85,format=auto/`);
  };

  useEffect(() => {
    async function fetchProjects() {
      try {
        const res = await fetch("https://api.dev.auxo.dk/public/content/projects");
        if (res.ok) {
          const data = await res.json();
          // Tag de seneste 3 projekter
          setLatestProjects(data.slice(0, 3));
        }
      } catch (err) {
        console.error("Fejl ved hentning af projekter:", err);
      } finally {
        setLoadingProjects(false);
      }
    }
    fetchProjects();
  }, []);

  const heroImageOriginal = "https://assets.auxodev.dk/assets/531e2906-0b4f-4a2d-bfb4-6b0bf7bd2bc6/image00005-rez_1.jpg";
  const heroSrcSet = `
    https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=95,format=auto/assets/531e2906-0b4f-4a2d-bfb4-6b0bf7bd2bc6/image00005-rez_1.jpg 600w,
    https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=95,format=auto/assets/531e2906-0b4f-4a2d-bfb4-6b0bf7bd2bc6/image00005-rez_1.jpg 1200w,
    https://assets.auxodev.dk/cdn-cgi/image/width=1800,quality=95,format=auto/assets/531e2906-0b4f-4a2d-bfb4-6b0bf7bd2bc6/image00005-rez_1.jpg 1800w
  `;

  return (
    <>
      <Helmet>
        <title>Henneberg Byggeri ApS</title>
        <meta
          name="description"
          content="Henneberg Byggeri ApS udfører professionelt byggeri, renovering, tagarbejde, nybyggeri, trækonstruktioner samt montering af døre og vinduer. Kontakt os for et uforpligtende tilbud."
        />
        <meta property="og:title" content="Henneberg Byggeri ApS" />
        <meta
          property="og:description"
          content="Henneberg Byggeri ApS udfører professionelt byggeri, renovering, tagarbejde, nybyggeri, trækonstruktioner samt montering af døre og vinduer."
        />
        <meta property="og:image" content={heroImageOriginal} />
        <link rel="canonical" href="https://hennebergbyggeri.dk/" />
      </Helmet>

      {/* Hero Sektion */}
      <section className="relative w-full h-[75vh] lg:h-[80vh] bg-neutral-950 text-white overflow-hidden flex items-center">
        {/* Full-bleed baggrundsbillede */}
        <div className="absolute inset-0 z-0">
          <img
            src={heroData.image}
            alt={heroData.title}
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
          {/* Mørkt overlay over hele billedet for perfekt læsbarhed */}
          <div className="absolute inset-0 bg-black/50 z-10"></div>
        </div>

        {/* Forgrundsindhold centreret ovenpå billedet */}
        <div className="relative z-20 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <span className="text-amber-500 font-bold uppercase tracking-wider text-sm sm:text-base block mb-3">
              {heroData.tag}
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold tracking-tight text-white leading-tight uppercase drop-shadow-2xl">
              {heroData.title}
            </h1>
            <p className="mt-4 text-base sm:text-lg text-neutral-200 max-w-xl drop-shadow-md">
              {heroData.description}
            </p>
            <div className="mt-8 flex flex-wrap gap-3 sm:gap-4">
              <a
                href="tel:31444011"
                className="rounded-none bg-white px-6 py-3 sm:px-8 sm:py-4 text-xs sm:text-sm font-bold uppercase tracking-wider text-neutral-950 transition-all duration-300 hover:bg-neutral-100 hover:shadow-lg active:scale-95"
              >
                RING DIREKTE: 31 44 40 11
              </a>
              <Link
                to="/kontakt"
                className="rounded-none bg-amber-600 px-6 py-3 sm:px-8 sm:py-4 text-xs sm:text-sm font-bold uppercase tracking-wider text-white transition-all duration-300 hover:bg-amber-700 hover:shadow-lg active:scale-95"
              >
                FÅ ET TILBUD
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Introduktion Sektion */}
      <section className="py-16 sm:py-24 bg-white text-neutral-900">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h2 className="mt-2 text-3xl font-extrabold tracking-tight text-neutral-900 sm:text-4xl uppercase">
              Din lokale tømrermester i Janderup Vestj
            </h2>
            <div className="mt-6 space-y-4 text-base text-gray-600 leading-relaxed">
              <p>
                Henneberg Byggeri ApS er en moderne dansk byggevirksomhed med dybe rødder i det vestjyske. Vi udfører alle former for tømrer-, murer- og snedkerarbejde for både private, erhverv og offentlige institutioner.
              </p>
              <p>
                Vores filosofi er enkel: Vi leverer solidt, holdbart håndværk til aftalt tid og pris. Vi går aldrig på kompromis med kvaliteten, uanset om vi skal skifte et enkelt vindue, lægge nyt tag eller opføre dit drømmehus fra bunden.
              </p>
              <p className="font-semibold text-neutral-900">
                Vi holder til på Vr Janderupvej 11 i Janderup Vestj, og dækker hele lokalområdet med stolthed og engagement.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Vores Ydelser Sektion - Bento Box Layout */}
      <section id="ydelser" className="py-16 sm:py-24 bg-neutral-50 text-neutral-900 scroll-mt-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            
            {/* Top-Left: Bygningsrenovering Image Card */}
            <Link
              to="/ydelser/bygningsrenovering"
              className="order-2 lg:order-1 relative bg-cover bg-center p-8 min-h-[280px] flex flex-col justify-between overflow-hidden group"
              style={{ backgroundImage: `url('https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/531e2906-0b4f-4a2d-bfb4-6b0bf7bd2bc6/image00029.jpeg')` }}
            >
              {/* Dark Overlay */}
              <div className="absolute inset-0 bg-black/60 transition-opacity group-hover:bg-black/40"></div>
              
              <div className="relative z-10 mt-auto">
                <h3 className="text-lg sm:text-xl font-bold text-white leading-snug uppercase">
                  Bygningsrenovering
                </h3>
                <p className="mt-1 text-xs text-gray-300">Nænsom fornyelse af ældre ejendomme</p>
              </div>

              {/* Dark accent circle with a light '+' icon */}
              <div className="absolute bottom-6 right-6 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-neutral-900/90 text-white border border-neutral-800 shadow-lg transition-transform group-hover:scale-110">
                <Plus className="h-5 w-5" />
              </div>
            </Link>

            {/* Top-Right: Large Introductory Block (spans 2 columns on lg) */}
            <div className="order-1 lg:order-2 md:col-span-2 bg-transparent p-4 lg:p-8 flex flex-col justify-center">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-[#1E2022] leading-tight uppercase">
                Professionelt byggeri med fokus på kvalitet og håndværk
              </h2>
              <p className="mt-4 text-base text-gray-600 max-w-2xl">
                Vi leverer solidt, holdbart håndværk til aftalt tid og pris. Vores ydelser dækker alt fra renovering og tagarbejde til nybyggeri og specialdesignede trækonstruktioner.
              </p>
              
              {/* CTA with Adjacent Circular Icon */}
              <div className="mt-8">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-3 text-xs font-bold uppercase tracking-wider text-[#1E2022] hover:text-[#C2A679] transition-colors group"
                >
                  <span>Få et uforpligtende tilbud</span>
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#434359] text-white transition-transform group-hover:translate-x-1">
                    <ArrowRight className="h-4 w-4" />
                  </div>
                </Link>
              </div>
            </div>

            {/* Bottom-Left: Nybyggeri Image Card */}
            <Link
              to="/ydelser/nybyggeri"
              className="order-3 lg:order-3 relative bg-cover bg-center p-8 min-h-[280px] flex flex-col justify-between overflow-hidden group"
              style={{ backgroundImage: `url('https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/531e2906-0b4f-4a2d-bfb4-6b0bf7bd2bc6/image00028.jpeg')` }}
            >
              {/* Dark Overlay */}
              <div className="absolute inset-0 bg-black/60 transition-opacity group-hover:bg-black/40"></div>
              
              <div className="relative z-10 mt-auto">
                <h3 className="text-lg sm:text-xl font-bold text-white leading-snug uppercase">
                  Nybyggeri
                </h3>
                <p className="mt-1 text-xs text-gray-300">Moderne lavenergi-huse & tilbygninger</p>
              </div>

              {/* Dark accent circle with a light '+' icon */}
              <div className="absolute bottom-6 right-6 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-neutral-900/90 text-white border border-neutral-800 shadow-lg transition-transform group-hover:scale-110">
                <Plus className="h-5 w-5" />
              </div>
            </Link>

            {/* Bottom-Center: Tagarbejde Image Card */}
            <Link
              to="/ydelser/tagarbejde"
              className="order-4 lg:order-4 relative bg-cover bg-center p-8 min-h-[280px] flex flex-col justify-between overflow-hidden group"
              style={{ backgroundImage: `url('https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/531e2906-0b4f-4a2d-bfb4-6b0bf7bd2bc6/image00012.jpeg')` }}
            >
              {/* Dark Overlay */}
              <div className="absolute inset-0 bg-black/60 transition-opacity group-hover:bg-black/40"></div>
              
              <div className="relative z-10 mt-auto">
                <h3 className="text-lg sm:text-xl font-bold text-white leading-snug uppercase">
                  Tagarbejde
                </h3>
                <p className="mt-1 text-xs text-gray-300">Nyt tag & efterisolering i høj kvalitet</p>
              </div>

              {/* Dark accent circle with a light '+' icon */}
              <div className="absolute bottom-6 right-6 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-neutral-900/90 text-white border border-neutral-800 shadow-lg transition-transform group-hover:scale-110">
                <Plus className="h-5 w-5" />
              </div>
            </Link>

            {/* Bottom-Right: Trækonstruktioner Image Card */}
            <Link
              to="/ydelser/traekonstruktioner"
              className="order-5 lg:order-5 relative bg-cover bg-center p-8 min-h-[280px] flex flex-col justify-between overflow-hidden group"
              style={{ backgroundImage: `url('https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/531e2906-0b4f-4a2d-bfb4-6b0bf7bd2bc6/image00035.jpeg')` }}
            >
              {/* Dark Overlay */}
              <div className="absolute inset-0 bg-black/60 transition-opacity group-hover:bg-black/40"></div>
              
              <div className="relative z-10 mt-auto">
                <h3 className="text-lg sm:text-xl font-bold text-white leading-snug uppercase">
                  Trækonstruktioner
                </h3>
                <p className="mt-1 text-xs text-gray-300">Specialdesignede terrasser & carporte</p>
              </div>

              {/* Dark accent circle with a light '+' icon */}
              <div className="absolute bottom-6 right-6 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-neutral-900/90 text-white border border-neutral-800 shadow-lg transition-transform group-hover:scale-110">
                <Plus className="h-5 w-5" />
              </div>
            </Link>

          </div>
        </div>
      </section>

      {/* Hvorfor vælge Henneberg Byggeri */}
      <section className="relative bg-[#1E2022] text-white overflow-hidden py-20 sm:py-28 lg:py-36">
        {/* Full-bleed baggrundsbillede med mørkt overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/531e2906-0b4f-4a2d-bfb4-6b0bf7bd2bc6/image00025.jpeg"
            alt="Henneberg Byggeri kvalitetshåndværk"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-neutral-950/85" />
        </div>

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="mt-2 text-3xl font-extrabold tracking-tight text-white sm:text-4xl uppercase">
              Hvorfor vælge Henneberg Byggeri?
            </h2>
            <p className="mt-4 text-base text-gray-300">
              Vi bygger på tillid, faglig stolthed og tæt dialog med vores kunder. Her er de fem grundpiller, som sikrer dig et perfekt resultat hver gang:
            </p>

            <div className="mt-10 flex flex-col sm:flex-row sm:flex-wrap items-center justify-center gap-x-8 gap-y-4">
              {whyChooseUs.slice(0, 3).map((item, index) => (
                <div key={index} className="flex items-center gap-2 bg-neutral-900/50 border border-neutral-800/40 px-4 py-2.5 backdrop-blur-sm">
                  <CheckCircle2 className="h-5 w-5 text-white shrink-0" />
                  <h3 className="text-xs sm:text-sm font-bold text-white uppercase tracking-wider">{item.title}</h3>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Udvalgte Projekter Sektion */}
      <section className="py-16 sm:py-24 bg-neutral-50 text-neutral-900">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-12">
            <div>
              <h2 className="mt-2 text-3xl font-extrabold tracking-tight text-neutral-900 sm:text-4xl uppercase">
                Udvalgte projekter
              </h2>
            </div>
            <Link
              to="/galleri"
              className="mt-4 sm:mt-0 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#C2A679] hover:underline"
            >
              Se hele galleriet
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {loadingProjects ? (
            <div className="flex justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#C2A679] border-t-transparent"></div>
            </div>
          ) : latestProjects.length === 0 ? (
            <p className="text-center text-gray-500">Ingen projekter fundet i øjeblikket.</p>
          ) : (
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {latestProjects.map((project) => (
                <div
                  key={project.id}
                  className="group relative overflow-hidden h-80 bg-gray-100 border border-gray-200 shadow-sm transition-all duration-300 hover:border-[#C2A679] hover:shadow-md"
                >
                  <img
                    src={`https://assets.auxodev.dk/cdn-cgi/image/width=500,quality=80,format=auto/assets/531e2906-0b4f-4a2d-bfb4-6b0bf7bd2bc6/${project.image?.[0]?.split("/").pop()}`}
                    alt={project.title?.da || project.title}
                    className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/0 transition-colors duration-300 group-hover:bg-black/10" />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Kontakt CTA Sektion */}
      <section className="py-16 sm:py-24 bg-white text-neutral-900">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 items-center">
            <div>
              <h2 className="mt-2 text-3xl font-extrabold tracking-tight text-neutral-900 sm:text-4xl uppercase">
                Lad os tage en uforpligtende snak om dit næste projekt
              </h2>
              <p className="mt-4 text-base text-gray-600 leading-relaxed">
                Går du med byggeplaner, eller har du en konkret opgave, der skal løses? Vi står altid klar til at rådgive dig og give et uforpligtende tilbud. Intet projekt er for stort eller for småt.
              </p>

              <div className="mt-8 space-y-4">
                <div className="flex items-center gap-3">
                  <div>
                    <span className="block text-sm font-bold text-neutral-900">Altid gratis rådgivning</span>
                    <span className="text-xs text-gray-500">Vi hjælper dig godt i gang med dine idéer.</span>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      {/* Anmeldelser Sektion */}
      <section className="py-10 sm:py-14 bg-neutral-50 text-neutral-900 overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="mt-2 text-3xl font-extrabold tracking-tight text-neutral-900 sm:text-4xl uppercase">
              Anmeldelser fra tilfredse bygherrer
            </h2>
          </div>

          <ReviewSlider />
        </div>
      </section>
    </>
  );
}
